import { injectable } from 'inversify';

import { ITeamLeagueTeamsRepository } from '@/domain/repositories/team-leagues-teams.repository.interface';
import { DivisionTeams } from '@/entities/DivisionTeam';
import { MovePlayersFromTeamResult } from '@/entities/MovePlayersFromTeamResult';
import { Result } from '@/entities/Result';
import { SearchParams } from '@/entities/SearchParams';
import { TeamInfo } from '@/entities/TeamLeagueTeamInfo';
import { TeamLeagueTeamsInfo } from '@/entities/TeamLeagueTeamsInfo';
import { teamStatsDtoToTeamStats } from '@/infrastructure/mapper/team-stats.mapper';
import { dropPlayersFromTeam, movePlayersFromTeam, playerJoinRequest } from '@/infrastructure/network/action/team-leagues-team-players-actions';
import { getTeamLeaguesTeams, listTeamLeaguesTeams } from '@/infrastructure/network/data/team-leagues-teams';
import { getTeamStats } from '@/infrastructure/network/data/team-stats';
import { getTeamsGroupedByDivision } from '@/infrastructure/network/data/teams-data';
import { TeamStatsDto } from '@/infrastructure/network/dto';

@injectable()
export class TeamLeagueTeamsRepository implements ITeamLeagueTeamsRepository {
	async getTeamLeaguesTeams(organizationUuid: string, teamLeagueUuid: string, teamUuid: string): Promise<Result<TeamInfo>> {
		const result = await getTeamLeaguesTeams({ organizationUuid, teamLeagueUuid, teamUuid });
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async listTeamLeaguesTeams(
		organizationUuid: string,
		teamLeagueUuid: string,
		searchParams?: SearchParams
	): Promise<Result<TeamLeagueTeamsInfo[]>> {
		const result = await listTeamLeaguesTeams({ organizationUuid, teamLeagueUuid, searchParams });
		if (result.statusCode === 200) {
			return { data: result.data.teamLeagueTeamsInfo, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}
	async movePlayersFromTeam(
		organizationUuid: string,
		teamLeagueUuid: string,
		teamUuid: string,
		playerUuids: string[],
		receivingTeamUuid: string
	): Promise<MovePlayersFromTeamResult> {
		const result = await movePlayersFromTeam({
			organizationUuid: organizationUuid,
			teamLeagueUuid: teamLeagueUuid,
			teamUuid: teamUuid,
			playerUuids: playerUuids,
			receivingTeamUuid: receivingTeamUuid
		});
		if (result.success) {
			return { success: result.success };
		} else {
			return { success: result.success, error: result.error };
		}
	}

	async playerJoinRequest(
		organizationUuid: string,
		teamLeaguesUuid: string,
		teamUuid: string,
		teamLeagueTradeRequestUuid: string,
		action: string
	): Promise<MovePlayersFromTeamResult> {
		const result = await playerJoinRequest({
			organizationUuid,
			teamLeaguesUuid,
			teamUuid,
			teamLeagueTradeRequestUuid,
			action
		});
		if (result.success) {
			return { success: result.success };
		} else {
			return { success: result.success, error: result.error };
		}
	}

	async dropPlayersFromTeam(
		organizationUuid: string,
		teamLeagueUuid: string,
		teamUuid: string,
		playerUuids: string[]
	): Promise<MovePlayersFromTeamResult> {
		const result = await dropPlayersFromTeam({
			organizationUuid: organizationUuid,
			teamLeagueUuid: teamLeagueUuid,
			teamUuid: teamUuid,
			playerUuids: playerUuids
		});
		if (result.success) {
			return { success: result.success };
		} else {
			return { success: result.success, error: result.error };
		}
	}

	async getTeamsGroupedByDivision(organizationUuid: string, teamLeagueUuid: string, searchParams: SearchParams): Promise<Result<DivisionTeams[]>> {
		const result = await getTeamsGroupedByDivision({ teamLeagueUuid, organizationUuid, searchParams });
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getTeamStats(organizationUuid: string, teamLeagueUuid: string, seasonUuid: string, teamUuid: string): Promise<Result<TeamStatsDto>> {
		const result = await getTeamStats({ teamLeagueUuid, organizationUuid, seasonUuid, teamUuid });
		if (result.statusCode === 200) {
			return { data: teamStatsDtoToTeamStats(result.data), statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}
}
